import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useCart } from '../context/CartContext';

import ProductCard from '../components/ProductCard';

import SearchBar from '../components/SearchBar';

import CategoryFilter from '../components/CategoryFilter';
import ProductPreview from '../components/ProductPreview';
import LoadMoreSentinel from '../components/LoadMoreSentinel';
import usePaginatedProductos from '../hooks/usePaginatedProductos';
import usePrefetchCatalogImages from '../hooks/usePrefetchCatalogImages';
import useOfflineImageHydration from '../hooks/useOfflineImageHydration';
import useFullPhotoVaultSync from '../hooks/useFullPhotoVaultSync';
import PhotoVaultBanner from '../components/PhotoVaultBanner';
import { isNativePhotoVault } from '../utils/imageCache';
import { categoriaLabel } from '../utils/categorias';

function getScrollEl() {
  return document.querySelector('.catalogo-body') || document.querySelector('.app-main');
}

function writeScroll(y) {
  const el = getScrollEl();
  if (el) el.scrollTop = y;
  else window.scrollTo(0, y);
}

export default function Catalogo({ onToast }) {
  const { dispatch } = useCart();

  const onToastRef = useRef(onToast);
  const scrollPos = useRef(0);

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoria, setCategoria] = useState('Todas');
  const [preview, setPreview] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('oh_catalog_view') === 'lista' ? 'lista' : 'fotos';
    } catch {
      return 'fotos';
    }
  });

  // Bóveda offline solo en APK. En web no se guardan fotos (evita CORS masivo).
  const nativeVault = isNativePhotoVault();
  usePrefetchCatalogImages({ enabled: nativeVault });
  const { progress: photoProgress, vaultCount } = useFullPhotoVaultSync({ enabled: nativeVault });
  useOfflineImageHydration({ enabled: false });

  function setCatalogView(mode) {
    setViewMode(mode);
    try {
      localStorage.setItem('oh_catalog_view', mode);
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const handleError = useCallback((message, type = 'error') => {
    onToastRef.current?.(message, type);
  }, []);

  const { productos, total, loading, loadingMore, hasMore, loadMore, shown } = usePaginatedProductos({
    q: debouncedQ,
    categoria,
    onError: handleError,
  });

  function openPreview(product) {
    const el = getScrollEl();
    scrollPos.current = el?.scrollTop ?? 0;
    setPreview(product);
  }

  function closePreview() {
    setPreview(null);
    requestAnimationFrame(() => {
      const el = getScrollEl();
      if (el) el.scrollTop = scrollPos.current;
    });
  }

  function addProduct(product, opts = {}) {
    const el = getScrollEl();
    if (el) scrollPos.current = el.scrollTop;
    dispatch({
      type: 'ADD',
      product,
      observaciones: opts.observaciones || '',
      cantidad: opts.cantidad || 1,
      opcion_id: opts.opcion_id || null,
      imagen: product.imagen,
    });
    onToastRef.current?.(`${opts.observaciones || product.codigo} agregado`, 'ok');
    requestAnimationFrame(() => {
      const main = getScrollEl();
      if (main) main.scrollTop = scrollPos.current;
    });
  }

  const catLabel = categoriaLabel(categoria);
  const countLabel =
    categoria === 'Todas'
      ? `${total} productos`
      : `${shown} de ${total} en ${catLabel}`;

  useEffect(() => {
    const main = getScrollEl();
    if (!main) return undefined;
    main.classList.add('catalogo-mode');
    return () => main.classList.remove('catalogo-mode');
  }, []);

  useLayoutEffect(() => {
    writeScroll(0);
  }, [categoria]);

  useEffect(() => {
    writeScroll(0);
    const id = window.requestAnimationFrame(() => writeScroll(0));
    return () => window.cancelAnimationFrame(id);
  }, [categoria]);

  return (
    <section className="catalogo-page">
      <div className="catalogo-top">
        <header className="page-header compact">
          <div className="page-header-row">
            <div>
              <h1>Nuevo pedido</h1>
              <p>{loading && !productos.length ? '…' : countLabel}</p>
            </div>
            <div className="view-toggle">
              <button
                type="button"
                className={`view-toggle-btn ${viewMode === 'fotos' ? 'active' : ''}`}
                onClick={() => setCatalogView('fotos')}
                title="Ver como fotos"
              >
                Fotos
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${viewMode === 'lista' ? 'active' : ''}`}
                onClick={() => setCatalogView('lista')}
                title="Ver con detalle"
              >
                Lista
              </button>
            </div>
          </div>
        </header>

        <div className="catalogo-sticky">
          <div className="catalogo-toolbar">
            <SearchBar value={q} onChange={setQ} onClear={() => setQ('')} />
          </div>

          <CategoryFilter
            value={categoria}
            onChange={(c) => {
              setCategoria(c);
              writeScroll(0);
            }}
          />
        </div>
      </div>

      {photoProgress && photoProgress.total > 0 ? (
        <PhotoVaultBanner progress={photoProgress} vaultCount={vaultCount} />
      ) : vaultCount > 0 ? (
        <PhotoVaultBanner progress={null} vaultCount={vaultCount} />
      ) : null}

      <div className="catalogo-body">
        {loading && !productos.length ? (
          <div className="loading-grid">Cargando catálogo...</div>
        ) : (
          <div className={`product-grid ${viewMode === 'fotos' ? 'product-grid-photos' : 'product-grid-list'}`}>
            {productos.map((p, idx) => (
              <ProductCard
                key={`${p.id ?? 'p'}-${p.codigo}`}
                product={p}
                variant={viewMode}
                priority={idx < 8}
                onPreview={openPreview}
                onAdd={(prod) => addProduct(prod)}
              />
            ))}
          </div>
        )}

        {!loading && !productos.length ? (
          <p className="empty-state">No hay productos con esa búsqueda o categoría</p>
        ) : null}

        {hasMore ? (
          <div className="load-more-wrap">
            <LoadMoreSentinel onVisible={loadMore} disabled={loadingMore || loading} />
            <button
              type="button"
              className="btn-secondary load-more-btn"
              disabled={loadingMore}
              onClick={loadMore}
            >
              {loadingMore ? 'Cargando…' : `Cargar más (${shown} de ${total})`}
            </button>
          </div>
        ) : null}
      </div>

      {preview ? (
        <ProductPreview
          product={preview}
          products={productos}
          onNavigate={setPreview}
          onClose={closePreview}
          onAdd={(prod, opts) => {
            addProduct(prod, opts);
          }}
        />
      ) : null}
    </section>
  );
}
